import React, { useState } from 'react';

interface CalendarProps {
  availableDates: string[];
  selectedDate: string | null;
  onSelectDate: (date: string) => void;
  monthsToShow?: number;
}

export default function Calendar({ availableDates, selectedDate, onSelectDate, monthsToShow = 3 }: CalendarProps) {
  const [currentDate, setCurrentDate] = useState(new Date());

  // Days that are available (0 = Sunday, 1 = Monday, etc.)
  const AVAILABLE_DAYS = [2, 3, 5]; // Tuesday (2), Wednesday (3), Friday (5)

  const isDateAvailable = (date: Date): boolean => {
    const dateString = date.toISOString().split('T')[0];
    return availableDates.includes(dateString);
  };

  const isDateDisabled = (date: Date): boolean => {
    const dayOfWeek = date.getDay();
    return !AVAILABLE_DAYS.includes(dayOfWeek);
  };

  const getDaysInMonth = (date: Date): number => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date): number => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const renderMonth = (monthOffset: number) => {
    const month = new Date(currentDate.getFullYear(), currentDate.getMonth() + monthOffset, 1);
    const daysInMonth = getDaysInMonth(month);
    const firstDay = getFirstDayOfMonth(month);
    const days = [];

    // Empty cells for days before month starts
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    // Days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(month.getFullYear(), month.getMonth(), i));
    }

    const monthName = month.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

    return (
      <div key={monthOffset} style={{ marginBottom: '2rem' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', textAlign: 'center' }}>
          {monthName}
        </h3>

        {/* Day headers */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '0.5rem', marginBottom: '0.5rem' }}>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div
              key={day}
              style={{
                textAlign: 'center',
                fontWeight: '600',
                fontSize: '0.75rem',
                color: '#6b7280',
                padding: '0.5rem',
              }}
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar days */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '0.5rem' }}>
          {days.map((day, idx) => {
            if (!day) {
              return (
                <div
                  key={`empty-${idx}`}
                  style={{
                    aspectRatio: '1',
                    backgroundColor: '#f9fafb',
                  }}
                />
              );
            }

            const dateString = day.toISOString().split('T')[0];
            const isDisabled = isDateDisabled(day);
            const isAvailable = isDateAvailable(day);
            const isSelected = selectedDate === dateString;
            const isPast = day < new Date(new Date().setHours(0, 0, 0, 0));

            let backgroundColor = '#fff';
            let borderColor = '#e5e7eb';
            let textColor = '#000';
            let cursor = 'default';

            if (isPast) {
              backgroundColor = '#f3f4f6';
              textColor = '#d1d5db';
            } else if (isDisabled) {
              backgroundColor = '#f3f4f6';
              textColor = '#d1d5db';
            } else if (isSelected) {
              backgroundColor = '#3b82f6';
              borderColor = '#2563eb';
              textColor = '#fff';
              cursor = 'pointer';
            } else if (isAvailable) {
              backgroundColor = '#f0fdf4';
              borderColor = '#86efac';
              cursor = 'pointer';
            }

            return (
              <button
                key={dateString}
                onClick={() => !isDisabled && !isPast && isAvailable && onSelectDate(dateString)}
                disabled={isDisabled || isPast || !isAvailable}
                style={{
                  aspectRatio: '1',
                  padding: '0.5rem',
                  border: `2px solid ${borderColor}`,
                  borderRadius: '0.375rem',
                  backgroundColor,
                  color: textColor,
                  cursor: isDisabled || isPast || !isAvailable ? 'not-allowed' : cursor,
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s',
                  opacity: isDisabled || isPast ? 0.5 : 1,
                }}
                title={
                  isPast ? 'Date has passed' : isDisabled ? 'Not available on this day' : isAvailable ? 'Available' : 'No slots available'
                }
              >
                {day.getDate()}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const months = [];
  for (let i = 0; i < monthsToShow; i++) {
    months.push(renderMonth(i));
  }

  return (
    <div
      style={{
        border: '1px solid #e5e7eb',
        borderRadius: '0.5rem',
        padding: '1.5rem',
        backgroundColor: '#fff',
      }}
    >
      <div style={{ marginBottom: '1rem', fontSize: '0.875rem', color: '#6b7280' }}>
        <div style={{ marginBottom: '0.5rem' }}>
          <span style={{ display: 'inline-block', width: '20px', height: '20px', backgroundColor: '#f0fdf4', border: '2px solid #86efac', borderRadius: '0.25rem', marginRight: '0.5rem' }} />
          Available
        </div>
        <div>
          <span style={{ display: 'inline-block', width: '20px', height: '20px', backgroundColor: '#f3f4f6', border: '2px solid #e5e7eb', borderRadius: '0.25rem', marginRight: '0.5rem' }} />
          Not available
        </div>
      </div>
      {months}
    </div>
  );
}
